import { useTripBuilder } from '../TripBuilderContext';
import { getTransportInfo, EFFORT_STYLE } from '../transport';

// Compact ribbon that shows the full trip shape at the top of every Plan
// flow step:
//   JFK → Lisbon (4n) ✈ Train · 3h → Porto (3n) → JFK
// For round-trip, just shows: JFK → Lisbon · 7 nights → JFK
//
// Each segment surfaces nights at the stop and the planned transport to the
// next stop, so the user sees the whole shape of their trip as they make
// each pick.
export default function RouteRibbon({ compact = false }) {
  const tb = useTripBuilder();
  const origin = tb.userConfig?.origin || 'JFK';

  if (!tb.destination) return null;

  if (!tb.isMultiCity) {
    const nights = tb.nights || 0;
    return (
      <div className={`bg-brand-ink/5 border border-brand-ink/10 rounded-xl px-3 py-2 flex items-center gap-2 text-sm overflow-x-auto ${compact ? 'text-xs' : ''}`}>
        <span className="font-bold text-brand-ink/65 shrink-0">Trip:</span>
        <span className="font-medium text-brand-ink shrink-0">{origin}</span>
        <span className="text-brand-ink/40">→</span>
        <span className="font-bold text-teal shrink-0">{tb.destination.city}</span>
        <span className="text-brand-ink/55 shrink-0">· {nights} night{nights === 1 ? '' : 's'}</span>
        <span className="text-brand-ink/40">→</span>
        <span className="font-medium text-brand-ink shrink-0">{origin}</span>
      </div>
    );
  }

  // Multi-city: render stops + transport between them
  const stops = tb.stops;
  return (
    <div className="bg-gradient-to-r from-teal/5 to-gold/5 border border-teal/20 rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm overflow-x-auto">
      <span className="font-bold text-teal text-xs uppercase tracking-wider shrink-0 mr-2">Multi-city</span>
      <span className="font-medium text-brand-ink shrink-0">{origin}</span>
      <span className="text-brand-ink/40 shrink-0">→</span>
      {stops.map((stop, i) => {
        const nextStop = stops[i + 1];
        const transport = nextStop ? getTransportInfo(stop.destination.city, nextStop.destination.city) : null;
        return (
          <div key={i} className="flex items-center gap-1.5 shrink-0">
            <span className="font-bold text-teal">{stop.destination.city}</span>
            <span className="text-brand-ink/55 text-xs">
              · {stop.nights}n
              {stop.hotel && <span className="ml-1 text-teal/70">🏨</span>}
              {stop.activities?.length > 0 && <span className="ml-0.5 text-brand-ink/55">·{stop.activities.length}🎟</span>}
            </span>
            {transport && (
              <>
                <span className="text-brand-ink/40">→</span>
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-brand-ink/10 text-brand-ink/70 shrink-0 inline-flex items-center gap-1"
                  title={`${transport.mode} · ${transport.duration} · ${transport.cost} · ${transport.note}`}
                >
                  <span>{transport.mode === 'Flight' ? '✈' : transport.mode === 'Train' || transport.mode === 'TGV' || transport.mode === 'Thalys' || transport.mode === 'Eurostar' ? '🚄' : transport.mode === 'Ferry/Flight' ? '⛴/✈' : '🚆'}</span>
                  <span>{transport.mode} · {transport.duration}</span>
                  {transport.effort && EFFORT_STYLE[transport.effort] && (
                    <span className={`px-1 py-px rounded text-[9px] font-bold ml-0.5 ${EFFORT_STYLE[transport.effort].class}`}>
                      {EFFORT_STYLE[transport.effort].label}
                    </span>
                  )}
                </span>
              </>
            )}
            {!nextStop && <span className="text-brand-ink/40">→</span>}
          </div>
        );
      })}
      <span className="font-medium text-brand-ink shrink-0">{origin}</span>
    </div>
  );
}
